import React, { useState, useEffect } from 'react';
import {
    CheckSquare,
    Calendar,
    BookOpen,
    Users,
    LayoutDashboard,
    Clock,
    AlertCircle,
    FileText,
    ExternalLink,
    ChevronRight,
    Menu,
    X,
    Plus,
    Trash2,
    Save,
    Briefcase,
    Target,
    Zap,
    List,
    History,
    Undo2,
    Search
} from 'lucide-react';

// --- DADOS INICIAIS ATUALIZADOS COM O FEEDBACK DETALHADO ---

const INITIAL_TASKS = [
    // PRÉ-INÍCIO (Refinado)
    { id: 1, title: 'Elaborar e imprimir horários e duties (provas, saída, clubs)', category: 'Pre-Inicio', priority: 'High', completed: false },
    { id: 2, title: 'Preencher planilha iSams (matérias e professores) da TE', category: 'Pre-Inicio', priority: 'High', completed: false },
    { id: 3, title: 'Seguir Checklist da Equipe de TE (passo a passo)', category: 'Pre-Inicio', priority: 'High', completed: false },
    { id: 4, title: 'Importar conteúdos no GSP (preparar para Inservice)', category: 'Pre-Inicio', priority: 'Medium', completed: false },
    { id: 5, title: 'Calendário Cycle Tests/Exames (incluir AAPPL, Evolucional, CAT4)', category: 'Pre-Inicio', priority: 'High', completed: false },
    { id: 6, title: 'Cadastrar alunos: Kognity, Savvas, Par, Evolucional, Cat4, Cambridge', category: 'Pre-Inicio', priority: 'High', completed: false },
    { id: 7, title: 'Finalizar Enturmação (atenção especial a Alunos Atletas)', category: 'Pre-Inicio', priority: 'High', completed: false },
    { id: 8, title: 'Imprimir e fixar listas de turmas e horários nas salas', category: 'Pre-Inicio', priority: 'Medium', completed: false },

    // INÍCIO DO ANO (Expandido)
    { id: 11, title: 'Organizar Meet the Teachers', category: 'Start-Of-Year', priority: 'High', completed: false },
    { id: 12, title: 'Agendar primeiro PTC (Parent Teacher Conference)', category: 'Start-Of-Year', priority: 'High', completed: false },
    { id: 13, title: 'Planejar Library Week (com Equipe de Linguagens)', category: 'Start-Of-Year', priority: 'Medium', completed: false },
    { id: 14, title: 'Primeiro Assembly: Código de conduta, rotina e expectativas', category: 'Start-Of-Year', priority: 'High', completed: false },
    { id: 15, title: 'Montar calendário de PD e Assemblies ASAP', category: 'Start-Of-Year', priority: 'High', completed: false },
    { id: 16, title: 'Realizar eleições de representantes de turma', category: 'Start-Of-Year', priority: 'Medium', completed: false },
    { id: 17, title: 'Marcar reuniões mensais com representantes de turma', category: 'Start-Of-Year', priority: 'Medium', completed: false },
    { id: 18, title: 'Marcar reuniões trimestrais com Mães Representantes', category: 'Start-Of-Year', priority: 'Medium', completed: false },
    { id: 19, title: 'Conferir entrega do material "Janelas Abertas" e acessos', category: 'Start-Of-Year', priority: 'Low', completed: false },

    // ROTINA / ONGOING (Refinado)
    { id: 20, title: 'Checar Agenda Edu (Manhã: Filtro Urgente / Tarde: Geral)', category: 'Routine', priority: 'High', completed: false },
    { id: 21, title: 'Ronda diária: Presença, Murais e Cartazes', category: 'Routine', priority: 'Medium', completed: false },
    { id: 22, title: 'Alinhamento Semanal (Enviar Sexta - Incluir part-timers)', category: 'Routine', priority: 'High', completed: false },
    { id: 23, title: 'Reunião Pedagógica Semanal com Area Leaders', category: 'Routine', priority: 'High', completed: false },
    { id: 24, title: 'Observação de aula (Feedback pedagógico)', category: 'Routine', priority: 'High', completed: false },
    { id: 25, title: 'Planejar PDs inspiradores (foco em alunos/currículo)', category: 'Routine', priority: 'Medium', completed: false },
    { id: 26, title: 'Contratar aplicadores para MYE/EYE (liberar professores)', category: 'Routine', priority: 'Medium', completed: false },
];

const ROUTINE_GUIDE = [
    { time: 'Início do Dia', activity: 'Filtro Agenda Edu', desc: 'Verificar mensagens urgentes antes de começar o dia operacional.', icon: 'AlertCircle' },
    { time: 'Manhã', activity: 'Ronda Estratégica', desc: 'Verificar presença, organização de murais, cartazes de regras e clima nos corredores.', icon: 'Users' },
    { time: 'Horário Protegido', activity: 'Atendimento & Observação', desc: 'Blocos blindados para atender famílias (pedagógico) ou observar aulas.', icon: 'BookOpen' },
    { time: 'Ao longo da semana', activity: 'Registro para Alinhamento', desc: 'Anotar pontos para o email de sexta (essencial para professores part-time).', icon: 'FileText' },
    { time: 'Sexta-feira', activity: 'Envio de Alinhamentos', desc: 'Disparar comunicações para Equipe, Alunos e Updates para Famílias.', icon: 'ExternalLink' }
];

const MACROPROCESSOS = [
    {
        area: 'Calendários',
        items: [
            'Calendário Escolar (Trimestres, MYE/EYE)',
            'Calendário de Cycle Tests (Internos/Externos)',
            'Calendário Interno (Arts Fest, COC, PTC, Café Pedagógico)'
        ],
        icon: 'Calendar',
        color: 'blue'
    },
    {
        area: 'Famílias',
        items: [
            'Reuniões Trimestrais (Mães Reps)',
            'Cafés Pedagógicos (GSP, Reports, CWW)',
            'Comunicação (Agenda Edu + Sway Updates)'
        ],
        icon: 'Users',
        color: 'green'
    },
    {
        area: 'Equipe',
        items: [
            'Observação de Aula (Bloquear agenda)',
            'PDs Estratégicos (Não burocráticos)',
            'Alinhamento Semanal (Processos e Datas)'
        ],
        icon: 'Briefcase',
        color: 'orange'
    },
    {
        area: 'Alunos',
        items: [
            'Disciplina (Reforço em Assemblies)',
            'Cultura e Valores (Representantes)',
            'Temas Estratégicos para Assemblies'
        ],
        icon: 'Target',
        color: 'purple'
    }
];

const ORGANIZATION_SYSTEM = [
    { title: 'Calendário Centralizado', desc: 'Google Calendar compartilhado (Cores diferentes para Cycle Tests, Eventos, PDs).', icon: 'Calendar' },
    { title: 'Quadro de Tarefas', desc: 'Trello/Asana com colunas: Pré-início, Início, Em andamento, Concluído.', icon: 'List' },
    { title: 'Templates de Comunicação', desc: 'Modelos prontos para Alinhamentos e Agenda Edu (padronização).', icon: 'FileText' },
    { title: 'Controle de Plataformas', desc: 'Planilha Mestra com status de cadastro de cada aluno em todas as plataformas.', icon: 'Zap' },
    { title: 'Repositório Padronizado', desc: 'Drive organizado: Calendários, PDs, Assemblies, Comunicados, Políticas.', icon: 'Briefcase' }
];

const IMPORTANT_LINKS = [
    { name: 'Site de Alinhamento da Coordenação', category: 'Equipe', url: '#' },
    { name: 'Sway Updates para Famílias', category: 'Famílias', url: 'https://docs.google.com/spreadsheets/d/14trXIu5d1_Nj_ckkCGC3zwkU0VBIw0H9ECDJxFGoNls/edit?gid=140625750#gid=140625750' },
    { name: 'GSP - Salas Virtuais', category: 'Pedagógico', url: '#' },
    { name: 'Agenda Edu', category: 'Comunicação', url: '#' },
    { name: 'Success Factors (RH)', category: 'Administrativo', url: '#' },
    { name: 'Drive Coordenação Fund 2', category: 'Arquivos', url: '#' },
    { name: 'Drive Professores Fund 2', category: 'Arquivos', url: '#' },
    { name: 'Site da Evolucional', category: 'Avaliações', url: '#' },
    { name: 'Plurall (Plataforma PAR)', category: 'Avaliações', url: '#' },
    { name: 'Site de Tech', category: 'Suporte', url: '#' },
    { name: 'Eleva Guide', category: 'Institucional', url: '#' },
    { name: 'Pipefy (Financeiro)', category: 'Administrativo', url: '#' },
    { name: 'Link Tree Escola', category: 'Geral', url: '#' },
    { name: 'Código de Conduta 2025', category: 'Política', url: '#' },
    { name: 'Family Handbook', category: 'Política', url: '#' },
    { name: 'Behaviour Policy', category: 'Política', url: '#' },
    { name: 'Mapa da Unidade 2026', category: 'Logística', url: '#' },
    { name: 'Planejamento Anual 2026', category: 'Planejamento', url: '#' },
    { name: 'Calendário Interno Barra 2026', category: 'Planejamento', url: 'https://docs.google.com/spreadsheets/d/1v_bdNSnHnCUXHMs2j4ozh4zcW2ioqkA_/edit?gid=762404560#gid=762404560' },
];

// --- COMPONENTES ---

const Card = ({ children, className = "" }) => (
    <div className={`bg-white rounded-lg shadow-sm border border-slate-200 ${className}`}>
        {children}
    </div>
);

const Badge = ({ type }) => {
    const styles = {
        'Pre-Inicio': 'bg-yellow-100 text-yellow-800',
        'Start-Of-Year': 'bg-blue-100 text-blue-800',
        'Routine': 'bg-green-100 text-green-800',
        'High': 'bg-red-50 text-red-600 border border-red-100',
        'Medium': 'bg-orange-50 text-orange-600 border border-orange-100',
        'Low': 'bg-slate-50 text-slate-600 border border-slate-100',
    };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[type] || 'bg-gray-100'}`}>{type}</span>;
};

export default function GestaoEscolar() {
    const [activeTab, setActiveTab] = useState('dashboard');
    const [tasks, setTasks] = useState(INITIAL_TASKS);
    const [importantLinks, setImportantLinks] = useState(IMPORTANT_LINKS);
    const [newTask, setNewTask] = useState('');
    const [newTaskCategory, setNewTaskCategory] = useState('Routine');
    const [sidebarOpen, setSidebarOpen] = useState(true);

    // Carregar Links da Planilha Google
    useEffect(() => {
        const fetchLinks = async () => {
            try {
                // Endpoint GVIZ retorna CSV com melhor suporte a CORS/Encoding do que o /export?format=csv
                const csvUrl = 'https://docs.google.com/spreadsheets/d/1pkvLkuxWRxlNNdZwIGJwA1hlfzuizIVbY4LAWou0VuA/gviz/tq?tqx=out:csv';

                const response = await fetch(csvUrl);
                if (!response.ok) throw new Error('Falha ao carregar planilha');

                const text = await response.text();

                // Parse CSV simples
                const lines = text.split('\n');
                // Remove cabeçalho e filtra linhas vazias
                const dataLines = lines.slice(1).filter(line => line.trim() !== '');

                const fetchedLinks = dataLines.map(line => {
                    // Regex para lidar com CSV corretamente (respeitando aspas)
                    const matches = line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || [];
                    // Fallback para split simples se o regex falhar ou formato for simples
                    const parts = matches.length >= 3 ? matches : line.split(',');

                    // Limpar aspas se houver
                    const clean = (str) => str ? str.replace(/^"|"$/g, '').trim() : '';

                    const name = clean(parts[0]);
                    const category = clean(parts[1]);
                    const url = clean(parts[2]); // A URL está na coluna C

                    if (!name) return null;

                    return { name, category, url };
                }).filter(item => item !== null);

                if (fetchedLinks.length > 0) {
                    console.log("Links atualizados via planilha:", fetchedLinks);
                    setImportantLinks(fetchedLinks);
                }
            } catch (error) {
                console.error("Erro ao buscar links da planilha:", error);
                // Mantém os links iniciais em caso de erro
            }
        };

        fetchLinks();
    }, []);

    // Carregar/Salvar tarefas no LocalStorage
    useEffect(() => {
        const saved = localStorage.getItem('coord-tasks-v2');
        if (saved) setTasks(JSON.parse(saved));
    }, []);

    useEffect(() => {
        localStorage.setItem('coord-tasks-v2', JSON.stringify(tasks));
    }, [tasks]);

    const toggleTask = (id) => {
        setTasks(tasks.map(t => {
            if (t.id === id) {
                const isCompleting = !t.completed;
                return {
                    ...t,
                    completed: isCompleting,
                    completedAt: isCompleting ? new Date().toISOString() : null
                };
            }
            return t;
        }));
    };

    const addTask = (e) => {
        e.preventDefault();
        if (!newTask.trim()) return;
        const task = {
            id: Date.now(),
            title: newTask,
            category: newTaskCategory,
            priority: 'Medium',
            completed: false
        };
        setTasks([...tasks, task]);
        setNewTask('');
    };

    const deleteTask = (id) => {
        setTasks(tasks.filter(t => t.id !== id));
    };

    const getProgress = (category) => {
        const categoryTasks = tasks.filter(t => t.category === category);
        if (categoryTasks.length === 0) return 0;
        const completed = categoryTasks.filter(t => t.completed).length;
        return Math.round((completed / categoryTasks.length) * 100);
    };

    // --- SUB-PÁGINAS ---

    const Dashboard = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-6 border-l-4 border-l-yellow-500">
                    <h3 className="text-gray-500 text-sm font-medium">Pré-Início</h3>
                    <div className="mt-2 flex items-end justify-between">
                        <span className="text-3xl font-bold text-slate-800">{getProgress('Pre-Inicio')}%</span>
                        <span className="text-sm text-slate-400 mb-1">Concluído</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                        <div className="bg-yellow-500 h-1.5 rounded-full" style={{ width: `${getProgress('Pre-Inicio')}%` }}></div>
                    </div>
                </Card>
                <Card className="p-6 border-l-4 border-l-blue-500">
                    <h3 className="text-gray-500 text-sm font-medium">Início do Ano</h3>
                    <div className="mt-2 flex items-end justify-between">
                        <span className="text-3xl font-bold text-slate-800">{getProgress('Start-Of-Year')}%</span>
                        <span className="text-sm text-slate-400 mb-1">Concluído</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                        <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${getProgress('Start-Of-Year')}%` }}></div>
                    </div>
                </Card>
                <Card className="p-6 border-l-4 border-l-green-500">
                    <h3 className="text-gray-500 text-sm font-medium">Rotina Contínua</h3>
                    <div className="mt-2 flex items-end justify-between">
                        <span className="text-3xl font-bold text-slate-800">{getProgress('Routine')}%</span>
                        <span className="text-sm text-slate-400 mb-1">Aderência</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                        <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${getProgress('Routine')}%` }}></div>
                    </div>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                            <AlertCircle size={20} className="text-red-500" />
                            Prioridades da Semana
                        </h2>
                    </div>
                    <div className="space-y-3">
                        {tasks.filter(t => t.priority === 'High' && !t.completed).slice(0, 5).map(task => (
                            <div key={task.id} className="flex items-start gap-3 p-3 bg-red-50/50 rounded-md border border-red-100">
                                <input
                                    type="checkbox"
                                    checked={task.completed}
                                    onChange={() => toggleTask(task.id)}
                                    className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                />
                                <span className="text-sm text-slate-700">{task.title}</span>
                            </div>
                        ))}
                        {tasks.filter(t => t.priority === 'High' && !t.completed).length === 0 && (
                            <p className="text-sm text-gray-500 italic">Nenhuma tarefa de alta prioridade pendente. Ótimo trabalho!</p>
                        )}
                    </div>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-slate-800 to-slate-900 text-white">
                    <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                        <Users size={20} />
                        Conselho do Gestor Sênior
                    </h2>
                    <div className="space-y-4 text-sm font-light leading-relaxed opacity-90">
                        <p>"Lembre-se: O papel aceita tudo, a rotina não. Seu maior erro será tentar fazer tudo sozinho."</p>
                        <p className="font-medium text-yellow-400">Dica de Ouro do Welcome Kit:</p>
                        <p>"A Mari (assistente) sabe tudo. Use o quadro da coordenação para distribuir tarefas entre você, a Mari e a coordenação de atendimento. Não centralize."</p>
                        <div className="mt-4 pt-4 border-t border-slate-700">
                            <p className="font-bold">Foco Estratégico:</p>
                            <p>Consulte a aba <strong>Estratégia</strong> para visualizar os Macroprocessos e implementar o sistema de organização sugerido.</p>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );

    const StrategyView = () => (
        <div className="space-y-8">
            <div>
                <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Target className="text-blue-600" />
                    Macroprocessos & Responsabilidades
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {MACROPROCESSOS.map((macro, idx) => (
                        <Card key={idx} className="p-5 border-t-4" style={{ borderColor: macro.color }}>
                            <div className="flex items-center gap-2 mb-3">
                                <div className={`p-2 rounded-lg bg-${macro.color}-100 text-${macro.color}-600`}>
                                    {macro.icon === 'Calendar' && <Calendar size={18} />}
                                    {macro.icon === 'Users' && <Users size={18} />}
                                    {macro.icon === 'Briefcase' && <Briefcase size={18} />}
                                    {macro.icon === 'Target' && <Target size={18} />}
                                </div>
                                <h3 className="font-bold text-slate-700">{macro.area}</h3>
                            </div>
                            <ul className="space-y-2">
                                {macro.items.map((item, i) => (
                                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                        <span className="mt-1.5 w-1.5 h-1.5 bg-slate-400 rounded-full flex-shrink-0"></span>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    ))}
                </div>
            </div>

            <div>
                <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Zap className="text-yellow-600" />
                    Sistema de Organização Sugerido
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {ORGANIZATION_SYSTEM.map((sys, idx) => (
                        <Card key={idx} className="p-4 hover:shadow-md transition-shadow">
                            <div className="mb-2 text-blue-600">
                                {sys.icon === 'Calendar' && <Calendar size={24} />}
                                {sys.icon === 'List' && <List size={24} />}
                                {sys.icon === 'FileText' && <FileText size={24} />}
                                {sys.icon === 'Zap' && <Zap size={24} />}
                                {sys.icon === 'Briefcase' && <Briefcase size={24} />}
                            </div>
                            <h3 className="font-bold text-slate-800 text-sm mb-1">{sys.title}</h3>
                            <p className="text-xs text-slate-500 leading-relaxed">{sys.desc}</p>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    );

    const TaskList = () => {
        const [filter, setFilter] = useState('all');

        const filteredTasks = tasks.filter(t => {
            if (filter === 'all') return true;
            return t.category === filter;
        });

        return (
            <Card className="flex-1 flex flex-col h-full">
                <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <CheckSquare className="text-blue-600" />
                        Checklists Operacionais
                    </h2>
                    <div className="flex gap-2">
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">Todas as Fases</option>
                            <option value="Pre-Inicio">Pré-Início</option>
                            <option value="Start-Of-Year">Início do Ano</option>
                            <option value="Routine">Rotina</option>
                        </select>
                    </div>
                </div>

                <div className="p-4 bg-slate-50 border-b border-slate-100">
                    <form onSubmit={addTask} className="flex gap-2">
                        <input
                            type="text"
                            value={newTask}
                            onChange={(e) => setNewTask(e.target.value)}
                            placeholder="Adicionar nova tarefa..."
                            className="flex-1 px-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        <select
                            value={newTaskCategory}
                            onChange={(e) => setNewTaskCategory(e.target.value)}
                            className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                        >
                            <option value="Pre-Inicio">Pré-Início</option>
                            <option value="Start-Of-Year">Início</option>
                            <option value="Routine">Rotina</option>
                        </select>
                        <button type="submit" className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors">
                            <Plus size={20} />
                        </button>
                    </form>
                </div>

                <div className="flex-1 overflow-y-auto p-2">
                    {filteredTasks.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                            <p>Nenhuma tarefa encontrada nesta categoria.</p>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {filteredTasks.map(task => (
                                <div
                                    key={task.id}
                                    className={`group flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0 ${task.completed ? 'opacity-50' : ''}`}
                                >
                                    <div className="flex items-center gap-3 flex-1">
                                        <button
                                            onClick={() => toggleTask(task.id)}
                                            className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${task.completed ? 'bg-green-500 border-green-500 text-white' : 'border-slate-300 hover:border-blue-500'}`}
                                        >
                                            {task.completed && <CheckSquare size={14} />}
                                        </button>
                                        <div className="flex flex-col">
                                            <span className={`text-sm ${task.completed ? 'line-through text-slate-400' : 'text-slate-700 font-medium'}`}>
                                                {task.title}
                                            </span>
                                            <div className="flex gap-2 mt-1">
                                                <Badge type={task.category} />
                                                {task.priority === 'High' && <Badge type="High" />}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => deleteTask(task.id)}
                                        className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-2"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Card>
        );
    };

    const RoutineView = () => (
        <div className="space-y-6">
            <Card className="p-6">
                <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                    <Clock className="text-blue-600" />
                    Rotina Blindada da Coordenação
                </h2>
                <div className="relative border-l-2 border-slate-200 ml-3 space-y-8">
                    {ROUTINE_GUIDE.map((item, index) => (
                        <div key={index} className="relative pl-8">
                            <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-white border-2 border-blue-500"></div>
                            <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wide mb-1">{item.time}</h3>
                            <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 hover:border-blue-200 transition-colors">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="font-bold text-slate-800">{item.activity}</span>
                                </div>
                                <p className="text-sm text-slate-600">{item.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            <Card className="p-6 bg-yellow-50 border-yellow-100">
                <h3 className="font-bold text-yellow-800 mb-2">Lembretes de Calendário (Events & Prazos)</h3>
                <ul className="list-disc list-inside text-sm text-yellow-800 space-y-1">
                    <li><strong>Semanal:</strong> Alinhamento Professores/Famílias (Sexta-feira)</li>
                    <li><strong>Mensal:</strong> Reunião com Representantes de Turma</li>
                    <li><strong>Trimestral:</strong> Café com Mães Representantes</li>
                    <li><strong>Anual:</strong> Library Week, Arts Fest, COC, PTC</li>
                    <li><strong>Avaliações:</strong> MYE/EYE (Contratar aplicadores antes!)</li>
                </ul>
            </Card>
        </div>
    );

    const ResourcesView = () => {
        const [searchTerm, setSearchTerm] = useState('');

        // Agrupar links por categoria
        const groupedLinks = importantLinks
            .filter(link => {
                if (!link.url || link.url === '#' || link.url === '') return false;
                if (!searchTerm) return true;
                const term = searchTerm.toLowerCase();
                return (
                    link.name.toLowerCase().includes(term) ||
                    link.category.toLowerCase().includes(term)
                );
            })
            .reduce((acc, link) => {
                if (!acc[link.category]) {
                    acc[link.category] = [];
                }
                acc[link.category].push(link);
                return acc;
            }, {});

        // Ordenar categorias (opcional, pode ser alfabético ou customizado)
        const categories = Object.keys(groupedLinks).sort();

        return (
            <Card className="min-h-full">
                <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                            <ExternalLink className="text-blue-600" />
                            Hub de Recursos & Links
                        </h2>
                        <p className="text-slate-500 text-sm mt-1">Acesso rápido aos documentos institucionais</p>
                    </div>
                    <div className="relative w-full md:w-64">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-slate-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Buscar documentos..."
                            className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm text-sm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="p-6 space-y-8">
                    {categories.map(category => (
                        <div key={category}>
                            <h3 className="text-lg font-bold text-slate-700 mb-4 border-l-4 border-blue-500 pl-3">{category}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {groupedLinks[category].map((link, idx) => (
                                    <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-all group bg-white shadow-sm">
                                        <div className="flex flex-col">
                                            <span className="font-medium text-slate-700 group-hover:text-blue-700">{link.name}</span>
                                        </div>
                                        <ExternalLink size={16} className="text-slate-300 group-hover:text-blue-500" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    ))}

                    {categories.length === 0 && (
                        <div className="text-center py-12 text-slate-400 flex flex-col items-center">
                            <Search size={32} className="mb-2 opacity-50" />
                            <p>Nenhum documento encontrado para "{searchTerm}".</p>
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="mt-2 text-blue-500 hover:text-blue-700 text-sm font-medium"
                                >
                                    Limpar busca
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </Card>
        );
    };

    const HistoryView = () => {
        const completedTasks = tasks.filter(t => t.completed).sort((a, b) => new Date(b.completedAt || 0) - new Date(a.completedAt || 0));

        return (
            <Card className="h-full flex flex-col">
                <div className="p-6 border-b border-slate-100">
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <History className="text-purple-600" />
                        Histórico de Atividades
                    </h2>
                    <p className="text-slate-500 text-sm mt-1">Registro cronológico de tarefas concluídas</p>
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                    {completedTasks.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                            <History size={48} className="mx-auto mb-4 opacity-50" />
                            <p>Nenhuma atividade registrada no histórico ainda.</p>
                        </div>
                    ) : (
                        <div className="relative border-l-2 border-purple-200 ml-3 space-y-8">
                            {completedTasks.map((task) => (
                                <div key={task.id} className="relative pl-8 group">
                                    <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-white border-2 border-purple-500"></div>
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-purple-50 p-4 rounded-lg border border-purple-100 hover:border-purple-300 transition-all">
                                        <div>
                                            <div className="text-xs font-bold text-purple-600 uppercase tracking-wide mb-1">
                                                {task.completedAt ? new Date(task.completedAt).toLocaleString('pt-BR') : 'Data não registrada'}
                                            </div>
                                            <h3 className="font-medium text-slate-800 text-lg">{task.title}</h3>
                                            <div className="flex gap-2 mt-2">
                                                <Badge type={task.category} />
                                                {task.priority === 'High' && <Badge type="High" />}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => toggleTask(task.id)}
                                            className="mt-4 sm:mt-0 flex items-center gap-2 px-3 py-2 bg-white text-slate-600 rounded-md border border-slate-200 hover:bg-slate-100 hover:text-red-600 transition-colors text-sm font-medium shadow-sm"
                                            title="Desfazer conclusão"
                                        >
                                            <Undo2 size={16} />
                                            Desfazer
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Card>
        );
    };

    return (
        <div className="flex h-screen bg-slate-100 text-slate-800 font-sans overflow-hidden">
            {/* Sidebar */}
            <aside className={`bg-slate-900 text-slate-300 flex-shrink-0 transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'} flex flex-col`}>
                <div className="p-6 flex items-center justify-between">
                    {sidebarOpen && <span className="text-white font-bold tracking-wider">COORD<span className="text-blue-400">UPPER</span></span>}
                    <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1 hover:bg-slate-800 rounded">
                        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>

                <nav className="flex-1 px-3 space-y-2 mt-4">
                    <button
                        onClick={() => setActiveTab('dashboard')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'dashboard' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' : 'hover:bg-slate-800'}`}
                    >
                        <LayoutDashboard size={20} />
                        {sidebarOpen && <span>Visão Geral</span>}
                    </button>
                    <button
                        onClick={() => setActiveTab('strategy')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'strategy' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' : 'hover:bg-slate-800'}`}
                    >
                        <Target size={20} />
                        {sidebarOpen && <span>Estratégia</span>}
                    </button>
                    <button
                        onClick={() => setActiveTab('tasks')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'tasks' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' : 'hover:bg-slate-800'}`}
                    >
                        <CheckSquare size={20} />
                        {sidebarOpen && <span>Checklists</span>}
                    </button>
                    <button
                        onClick={() => setActiveTab('routine')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'routine' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' : 'hover:bg-slate-800'}`}
                    >
                        <Clock size={20} />
                        {sidebarOpen && <span>Rotina</span>}
                    </button>
                    <button
                        onClick={() => setActiveTab('resources')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'resources' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' : 'hover:bg-slate-800'}`}
                    >
                        <BookOpen size={20} />
                        {sidebarOpen && <span>Links & Docs</span>}
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'history' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' : 'hover:bg-slate-800'}`}
                    >
                        <History size={20} />
                        {sidebarOpen && <span>Histórico</span>}
                    </button>
                </nav>

                <div className="p-4 border-t border-slate-800">
                    {sidebarOpen ? (
                        <div className="text-xs text-slate-500">
                            <p>Fundamental II</p>
                            <p>Planejamento 2026</p>
                        </div>
                    ) : (
                        <div className="flex justify-center text-xs text-slate-500">F2</div>
                    )}
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 flex flex-col overflow-hidden">
                <header className="bg-white border-b border-slate-200 p-6 flex justify-between items-center">
                    <h1 className="text-2xl font-bold text-slate-800">
                        {activeTab === 'dashboard' && 'Painel de Controle'}
                        {activeTab === 'strategy' && 'Estratégia & Macroprocessos'}
                        {activeTab === 'tasks' && 'Plano de Ação e Tarefas'}
                        {activeTab === 'routine' && 'Fluxo de Rotina Semanal'}
                        {activeTab === 'resources' && 'Documentos Institucionais'}
                        {activeTab === 'history' && 'Histórico de Realizações'}
                    </h1>
                    <div className="text-sm text-slate-500 hidden sm:block">
                        {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto p-6">
                    <div className="max-w-6xl mx-auto h-full">
                        {activeTab === 'dashboard' && <Dashboard />}
                        {activeTab === 'strategy' && <StrategyView />}
                        {activeTab === 'tasks' && <TaskList />}
                        {activeTab === 'routine' && <RoutineView />}
                        {activeTab === 'resources' && <ResourcesView />}
                        {activeTab === 'history' && <HistoryView />}
                    </div>
                </div>
            </main>
        </div>
    );
}
